import {Component, computed, ElementRef, inject, PLATFORM_ID, signal, viewChild} from '@angular/core'
import {DOCUMENT, isPlatformBrowser} from '@angular/common'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {ActivatedRoute, Router, RouterLink} from '@angular/router'
import {LogoComponent} from '../logo/logo.component'
import {Comparison, COMPARISONS, getComparison} from './comparison.data'

/**
 * Renders any /vs/:slug comparison page from COMPARISONS.
 *
 * One component for every competitor rather than a page each: the layout is the
 * argument (headline contrast, then priced arithmetic, then an honest concession),
 * and it should not drift between pages. Adding a competitor means adding an entry
 * to comparison.data.ts and nothing else.
 *
 * SEO metadata is supplied by comparisonSeoResolver, so the served HTML already
 * carries the right title, canonical and schema before any JS runs.
 */
@Component({
  selector: 'app-comparison',
  standalone: true,
  imports: [RouterLink, LogoComponent],
  templateUrl: './comparison.component.html',
  styleUrl: './comparison.component.scss'
})
export class ComparisonComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly doc = inject(DOCUMENT)
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))

  /**
   * The element that actually scrolls. Landing-style pages pin a full-screen
   * wrapper and scroll `.page-container` inside it (see page-common.scss), with
   * the body set to overflow: hidden — so the window never moves and
   * ViewportScroller has nothing to scroll.
   */
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller')

  readonly comparison = signal<Comparison | undefined>(undefined)

  /** The other comparisons, for cross-linking. Internal links help these pages rank. */
  readonly others = computed(() =>
    COMPARISONS.filter(c => c.slug !== this.comparison()?.slug)
  )

  readonly year = new Date().getFullYear()

  constructor() {
    // Follow the param rather than reading the snapshot once: moving between
    // /vs/a and /vs/b reuses this instance, so a snapshot read would leave the
    // old competitor on screen under the new URL. paramMap replays its current
    // value on subscribe, so the first render (including SSR) is unaffected.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const isSwitch = this.comparison() !== undefined
      const comparison = getComparison(params.get('slug'))
      this.comparison.set(comparison)

      // An unknown slug is not a page — send it home rather than rendering an empty
      // shell that Google would index as thin content.
      if (!comparison) {
        void this.router.navigateByUrl('/')
        return
      }
      // The cross-links sit at the bottom of the page; without this the new
      // comparison opens scrolled to its footer.
      if (isSwitch && this.isBrowser) {
        this.scroller()?.nativeElement.scrollTo({top: 0})
      }
    })

    if (this.isBrowser) {
      this.doc.body.classList.add('landing-page-active')
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      this.doc.body.classList.remove('landing-page-active')
    }
  }
}
